import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import ApplicationReviewTab from "./ApplicationReviewTab";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
    padding: "1.5rem",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "1.25rem",
    flexWrap: "wrap",
    gap: "0.75rem",
  },
  title: { fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 4 },
  subtitle: { fontSize: 13, color: "#6b7280" },
  headerActions: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  pill: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 6,
    border: "none",
    background: "#16A34A",
    color: "#fff",
    cursor: "pointer",
  },
  btnDanger: {
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 6,
    border: "1px solid #DC2626",
    background: "transparent",
    color: "#DC2626",
    cursor: "pointer",
  },
  btnSecondary: {
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 5,
  },
  spinnerDanger: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid rgba(220,38,38,0.25)",
    borderTopColor: "#DC2626",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 5,
  },
  warnBanner: {
    fontSize: 13,
    color: "#92400E",
    background: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: "1rem",
  },
  errorBanner: {
    fontSize: 13,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: "1rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  infoBanner: {
    fontSize: 13,
    color: "#374151",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: "1rem",
  },
  // Cards
  card: {
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  cardHead: {
    padding: "11px 14px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
  },
  cardBody: { padding: "12px 14px" },
  // InfoRow
  infoRow: {
    display: "flex",
    gap: 6,
    fontSize: 13,
    marginBottom: 5,
    alignItems: "flex-start",
  },
  infoLabel: { fontWeight: 600, color: "#374151", minWidth: 130, flexShrink: 0 },
  infoValue: { color: "#6b7280" },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginTop: 10,
    marginBottom: 5,
  },
  emptyText: { fontSize: 13, color: "#9ca3af" },
  // Grids
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "1rem",
    marginBottom: "1rem",
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: "1rem",
    marginBottom: "1rem",
  },
  refItem: {
    borderTop: "1px solid #f3f4f6",
    paddingTop: 8,
    marginTop: 8,
  },
  vehicleItem: {
    borderTop: "1px solid #f3f4f6",
    paddingTop: 6,
    marginTop: 6,
  },
  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    backdropFilter: "blur(2px)",
    zIndex: 1060,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1rem",
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    display: "flex",
    flexDirection: "column",
  },
  modalHead: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { fontSize: 15, fontWeight: 600, color: "#111827" },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    lineHeight: 1,
    cursor: "pointer",
    color: "#6b7280",
    padding: "0 4px",
  },
  modalBody: { padding: "1.1rem 1.25rem" },
  modalFoot: {
    padding: "0.85rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
  fieldGroup: { marginBottom: 14 },
  formLabel: { fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4, display: "block" },
  formInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    outline: "none",
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────
const pillColors = {
  pending:          { background: "#FEF3C7", color: "#92400E" },
  under_review:     { background: "#DBEAFE", color: "#1E40AF" },
  approved:         { background: "#D1FAE5", color: "#065F46" },
  rejected:         { background: "#FEE2E2", color: "#991B1B" },
  withdrawn:        { background: "#FEE2E2", color: "#991B1B" },
  property_deleted: { background: "#F3F4F6", color: "#374151" },
  property_removed: { background: "#F3F4F6", color: "#374151" },
};

const Pill = ({ status, label }) => {
  const colors = pillColors[status] || { background: "#F3F4F6", color: "#374151" };
  const text = label || (status || "unknown").replace(/_/g, " ");
  return <span style={{ ...s.pill, ...colors }}>{text}</span>;
};

const InfoRow = ({ label, value }) => (
  <div style={s.infoRow}>
    <span style={s.infoLabel}>{label}</span>
    <span style={s.infoValue}>{value || "-"}</span>
  </div>
);

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
};

// ── Component ──────────────────────────────────────────────────────────────────
const ApplicationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [leaseStart, setLeaseStart] = useState("");
  const [leaseEnd, setLeaseEnd] = useState("");
  const [acceptFormError, setAcceptFormError] = useState("");

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(API_ENDPOINTS.applications.getById(id), {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
            ...getAuthHeaders(),
          },
        });
        if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
        const data = await res.json();
        setApplication(data.data || null);
      } catch (err) {
        console.error("Error fetching application:", err);
        setError(err.message || "Failed to load application");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchApplication();
  }, [id, getAuthHeaders]);

  const toInputDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const getDefaultLeaseDates = () => {
    const start = new Date();
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    return { start: toInputDate(start), end: toInputDate(end) };
  };

  const handleBackNavigation = () => {
    if (window.history.length > 1) { navigate(-1); return; }
    navigate("/applications");
  };

  const handleOpenAcceptModal = () => {
    const defaults = getDefaultLeaseDates();
    setLeaseStart(defaults.start);
    setLeaseEnd(defaults.end);
    setAcceptFormError("");
    setShowAcceptModal(true);
  };

  const handleAccept = async () => {
    if (!application?._id) return;
    if (!leaseStart || !leaseEnd) { setAcceptFormError("Lease start and lease end are required."); return; }
    if (new Date(leaseEnd) < new Date(leaseStart)) { setAcceptFormError("Lease end must be on or after lease start."); return; }
    setActionLoading("accept");
    setActionError(null);
    setAcceptFormError("");
    try {
      const res = await fetch(API_ENDPOINTS.applications.accept(application._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ leaseStart, leaseEnd }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.errors?.[0]?.msg || `Error ${res.status}`);
      setApplication(data.data || application);
      setShowAcceptModal(false);
    } catch (err) {
      console.error("Accept application error:", err);
      setActionError(err.message || "Failed to accept application");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async () => {
    if (!application?._id) return;
    setActionLoading("decline");
    setActionError(null);
    try {
      const res = await fetch(API_ENDPOINTS.applications.updateStatus(application._id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ status: "rejected" }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || data?.errors?.[0]?.msg || `Error ${res.status}`);
      setApplication(data.data || application);
    } catch (err) {
      console.error("Decline application error:", err);
      setActionError(err.message || "Failed to decline application");
    } finally {
      setActionLoading(null);
    }
  };

  // ── Loading / Error / Not Found ──────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center", padding: "4rem" }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <span style={{ ...s.spinner, borderTopColor: "#6b7280", border: "2px solid #e5e7eb", borderTopColor: "#6b7280", width: 18, height: 18 }} />
        <span style={{ fontSize: 14, color: "#6b7280", marginLeft: 8 }}>Loading application…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={s.errorBanner}>
          <div>
            <strong>Unable to load application</strong>
            <p style={{ margin: "4px 0 0" }}>{error}</p>
          </div>
        </div>
        <button style={s.btnSecondary} onClick={handleBackNavigation}>Back</button>
      </div>
    );
  }

  if (!application) {
    return (
      <div style={s.page}>
        <div style={s.warnBanner}>
          <strong>Application not found</strong>
          <p style={{ margin: "4px 0 0" }}>The requested application could not be found.</p>
        </div>
        <button style={s.btnSecondary} onClick={handleBackNavigation}>Back</button>
      </div>
    );
  }

  // ── Derived state ──────────────────────────────────────────────────────────
  const tenant = application.tenant || {};
  const property = application.property || {};
  const spouse = application.spouse || {};
  const spouseNextOfKin = application.spouseNextOfKin || {};
  const nextOfKin = application.nextOfKin || {};
  const currentResidence = application.currentResidence || {};
  const previousResidence = application.previousResidence || {};
  const pets = application.pets || {};
  const vehicles = application.vehicles || [];
  const references = application.references || [];
  const sourceOfFunds = application.sourceOfFunds || {};

  const propertyRemovedFromList =
    application.status === "property_deleted" || application.property?.isDeleted;
  const canAcceptOrDecline =
    application &&
    (application.status === "pending" || application.status === "under_review") &&
    !propertyRemovedFromList;

  const statusKey = propertyRemovedFromList ? "property_removed" : application.status;
  const statusLabel = propertyRemovedFromList ? "Property Removed" : undefined;

  return (
    <div style={s.page} className="re-detail">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Accept Lease Modal ────────────────────────────────────────────────── */}
      {showAcceptModal && (
        <div style={s.overlay} onClick={(e) => e.target === e.currentTarget && actionLoading !== "accept" && setShowAcceptModal(false)}>
          <div style={s.modal}>
            <div style={s.modalHead}>
              <span style={s.modalTitle}>Confirm Lease Dates</span>
              <button style={s.closeBtn} onClick={() => setShowAcceptModal(false)} disabled={actionLoading === "accept"}>×</button>
            </div>
            <div style={s.modalBody}>
              {acceptFormError && (
                <div style={{ ...s.errorBanner, marginBottom: 12 }}>{acceptFormError}</div>
              )}
              <div style={s.fieldGroup}>
                <label style={s.formLabel}>Lease Start</label>
                <input
                  style={s.formInput}
                  type="date"
                  value={leaseStart}
                  onChange={(e) => setLeaseStart(e.target.value)}
                  disabled={actionLoading === "accept"}
                />
              </div>
              <div style={{ ...s.fieldGroup, marginBottom: 0 }}>
                <label style={s.formLabel}>Lease End</label>
                <input
                  style={s.formInput}
                  type="date"
                  value={leaseEnd}
                  onChange={(e) => setLeaseEnd(e.target.value)}
                  disabled={actionLoading === "accept"}
                />
              </div>
            </div>
            <div style={s.modalFoot}>
              <button style={s.btnSecondary} onClick={() => setShowAcceptModal(false)} disabled={actionLoading === "accept"}>
                Cancel
              </button>
              <button style={s.btnPrimary} onClick={handleAccept} disabled={actionLoading === "accept"}>
                {actionLoading === "accept" ? (
                  <><span style={s.spinner} />Accepting…</>
                ) : "Accept Application"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ───────────────────────────────────────────────────────── */}
      <div style={s.header}>
        <div>
          <div style={s.title}>Application {application.applicationId || ""}</div>
          <div style={s.subtitle}>Detailed view of the rental application and supporting documents.</div>
        </div>
        <div style={s.headerActions}>
          <Pill status={statusKey} label={statusLabel} />
          {canAcceptOrDecline && (
            <>
              <button style={s.btnPrimary} onClick={handleOpenAcceptModal} disabled={!!actionLoading}>
                {actionLoading === "accept" ? (
                  <><span style={s.spinner} />Accepting…</>
                ) : "Accept"}
              </button>
              <button style={s.btnDanger} onClick={handleDecline} disabled={!!actionLoading}>
                {actionLoading === "decline" ? (
                  <><span style={s.spinnerDanger} />Declining…</>
                ) : "Decline"}
              </button>
            </>
          )}
          <button style={s.btnSecondary} onClick={handleBackNavigation}>Back to list</button>
        </div>
      </div>

      {/* ── Banners ───────────────────────────────────────────────────────────── */}
      {propertyRemovedFromList && (
        <div style={s.warnBanner}>
          This application is read-only: the linked property was removed from your listings. You can
          still review submitted information and see the property name below; accept, decline, and
          review edits are disabled.
        </div>
      )}
      {actionError && (
        <div style={s.errorBanner}>
          <span>{actionError}</span>
          <button style={{ background: "none", border: "none", cursor: "pointer", color: "#991B1B", fontSize: 16 }} onClick={() => setActionError(null)}>×</button>
        </div>
      )}

      {/* ── Row 1: Applicant · Property · Meta ───────────────────────────────── */}
      <div style={s.grid3}>

        {/* Applicant card */}
        <div style={s.card}>
          <div style={s.cardHead}>Applicant</div>
          <div style={s.cardBody}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#111827", marginBottom: 2 }}>
              {application.applicantFullName ||
                `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim() || "-"}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
              {tenant.email || "-"}
              {tenant.phoneMobile ? ` • ${tenant.phoneMobile}` : ""}
            </div>
            <InfoRow label="Date of Birth" value={formatDate(tenant.dateOfBirth)} />
            <InfoRow label="Marital Status" value={tenant.maritalStatus} />
            <InfoRow
              label="Children"
              value={tenant.numberOfChildren != null
                ? `${tenant.numberOfChildren}${tenant.childrenAges ? ` (Ages: ${tenant.childrenAges})` : ""}`
                : undefined}
            />
            {tenant.address && <InfoRow label="Address" value={tenant.address} />}
            {tenant.employment && (
              <>
                <div style={s.sectionTitle}>Employment</div>
                <InfoRow label="Occupation" value={tenant.employment.occupation} />
                <InfoRow label="Employer" value={tenant.employment.employer} />
                {tenant.employment.duration && <InfoRow label="Duration" value={tenant.employment.duration} />}
                <InfoRow
                  label="Monthly Income"
                  value={tenant.employment.monthlyIncome != null
                    ? `TTD ${Number(tenant.employment.monthlyIncome).toLocaleString()}`
                    : undefined}
                />
                {tenant.employment.employerAddress && <InfoRow label="Employer Address" value={tenant.employment.employerAddress} />}
                {tenant.employment.workPhone && <InfoRow label="Work Phone" value={tenant.employment.workPhone} />}
              </>
            )}
          </div>
        </div>

        {/* Property card */}
        <div style={s.card}>
          <div style={s.cardHead}>Property</div>
          <div style={s.cardBody}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "#111827", marginBottom: 2 }}>
              {property.title || "Untitled property"}
            </div>
            {property.address && (
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
                {[property.address.street, property.address.city, property.address.country]
                  .filter(Boolean)
                  .join(", ")}
              </div>
            )}
            <InfoRow label="Type" value={property.type} />
            {property.pricing && (
              <InfoRow
                label="Proposed Rent"
                value={property.pricing.rentAmount != null
                  ? `TTD ${Number(property.pricing.rentAmount).toLocaleString()}`
                  : undefined}
              />
            )}
          </div>
        </div>

        {/* Meta card */}
        <div style={s.card}>
          <div style={s.cardHead}>Application Meta</div>
          <div style={s.cardBody}>
            <InfoRow label="Application ID" value={application.applicationId} />
            <InfoRow label="Submitted" value={formatDate(application.submittedAt)} />
            <InfoRow
              label="Application Age"
              value={application.applicationAge != null ? `${application.applicationAge} days` : undefined}
            />
            {application.reviewedAt && (
              <InfoRow label="Reviewed" value={formatDate(application.reviewedAt)} />
            )}
          </div>
        </div>
      </div>

      {/* ── Row 2: Review Tab ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: "1rem" }}>
        <ApplicationReviewTab
          application={application}
          onApplicationUpdated={setApplication}
          readOnly={propertyRemovedFromList}
        />
      </div>

      {/* ── Row 3: Spouse · Next of Kin · Source of Funds ────────────────────── */}
      <div style={s.grid3}>

        {/* Spouse / Partner */}
        <div style={s.card}>
          <div style={s.cardHead}>Spouse / Partner</div>
          <div style={s.cardBody}>
            {spouse && (spouse.firstName || spouse.lastName || spouse.email || spouse.phoneMobile) ? (
              <>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 6 }}>
                  {[spouse.firstName, spouse.lastName].filter(Boolean).join(" ") || "-"}
                </div>
                <InfoRow label="Date of Birth" value={formatDate(spouse.dateOfBirth)} />
                {spouse.age != null && <InfoRow label="Age" value={spouse.age} />}
                {spouse.address && <InfoRow label="Address" value={spouse.address} />}
                {(spouse.email || spouse.phoneMobile) && (
                  <InfoRow label="Contact" value={[spouse.email, spouse.phoneMobile].filter(Boolean).join(" • ")} />
                )}
                {spouse.occupation && <InfoRow label="Occupation" value={spouse.occupation} />}
                {spouse.employment && (
                  <>
                    <div style={s.sectionTitle}>Employment</div>
                    <InfoRow label="Employer" value={spouse.employment.employer} />
                    {spouse.employment.employerAddress && <InfoRow label="Employer Address" value={spouse.employment.employerAddress} />}
                    {spouse.employment.monthlyIncome != null && (
                      <InfoRow label="Monthly Income" value={`TTD ${Number(spouse.employment.monthlyIncome).toLocaleString()}`} />
                    )}
                    {spouse.employment.workPhone && <InfoRow label="Work Phone" value={spouse.employment.workPhone} />}
                  </>
                )}
                {(spouseNextOfKin.name || spouseNextOfKin.phone) && (
                  <>
                    <div style={s.sectionTitle}>Spouse&apos;s Next of Kin</div>
                    <InfoRow label="Name" value={spouseNextOfKin.name} />
                    <InfoRow label="Relationship" value={spouseNextOfKin.relationship} />
                    {spouseNextOfKin.address && <InfoRow label="Address" value={spouseNextOfKin.address} />}
                    {spouseNextOfKin.phone && <InfoRow label="Phone" value={spouseNextOfKin.phone} />}
                  </>
                )}
              </>
            ) : (
              <div style={s.emptyText}>No spouse / partner information provided.</div>
            )}
          </div>
        </div>

        {/* Next of Kin */}
        <div style={s.card}>
          <div style={s.cardHead}>Next of Kin</div>
          <div style={s.cardBody}>
            {nextOfKin && (nextOfKin.name || nextOfKin.phone || nextOfKin.address) ? (
              <>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 6 }}>
                  {nextOfKin.name}
                </div>
                <InfoRow label="Relationship" value={nextOfKin.relationship} />
                {nextOfKin.address && <InfoRow label="Address" value={nextOfKin.address} />}
                {nextOfKin.phone && <InfoRow label="Phone" value={nextOfKin.phone} />}
              </>
            ) : (
              <div style={s.emptyText}>No next of kin information provided.</div>
            )}
          </div>
        </div>

        {/* Source of Funds */}
        <div style={s.card}>
          <div style={s.cardHead}>Source of Funds</div>
          <div style={s.cardBody}>
            {sourceOfFunds?.sources?.length > 0 ? (
              <>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: "0.5rem" }}>
                  Declared funding sources
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: sourceOfFunds.otherDetails ? "0.75rem" : 0 }}>
                  {sourceOfFunds.sources.map((src) => {
                    const labels = {
                      salary: "Salary / Wages",
                      self_employment: "Self-Employment",
                      pension: "Pension / Retirement",
                      sale_of_property: "Sale of Property",
                      investments: "Investments / Dividends",
                      bank_loan: "Bank Loan / Mortgage",
                      rental_income: "Rental Income",
                      family_support: "Family Support",
                      other: "Other",
                    };
                    return (
                      <span
                        key={src}
                        style={{ ...s.pill, background: "#dbeafe", color: "#1d4ed8", textTransform: "none", fontWeight: 500, fontSize: 12, letterSpacing: 0 }}
                      >
                        {labels[src] || src}
                      </span>
                    );
                  })}
                </div>
                {sourceOfFunds.otherDetails && (
                  <InfoRow label="Other details" value={sourceOfFunds.otherDetails} />
                )}
              </>
            ) : (
              <div style={s.emptyText}>No source of funds information provided.</div>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 4: Residence History · References, Pets & Vehicles ───────────── */}
      <div style={s.grid2}>

        {/* Residence History */}
        <div style={s.card}>
          <div style={s.cardHead}>Residence History</div>
          <div style={s.cardBody}>
            <div style={s.sectionTitle}>Current Residence</div>
            <InfoRow label="Landlord" value={currentResidence.landlordName} />
            {currentResidence.landlordAddress && <InfoRow label="Address" value={currentResidence.landlordAddress} />}
            {currentResidence.landlordPhone && <InfoRow label="Phone" value={currentResidence.landlordPhone} />}
            <InfoRow label="How long" value={currentResidence.howLongAtAddress} />
            {currentResidence.rentalRate != null && (
              <InfoRow label="Rental Rate" value={`TTD ${Number(currentResidence.rentalRate).toLocaleString()}`} />
            )}
            <InfoRow label="Reason for leaving" value={currentResidence.reasonForLeaving} />

            <div style={{ ...s.sectionTitle, marginTop: 14 }}>Previous Residence</div>
            <InfoRow label="Landlord" value={previousResidence.landlordName} />
            {previousResidence.landlordAddress && <InfoRow label="Address" value={previousResidence.landlordAddress} />}
            {previousResidence.landlordPhone && <InfoRow label="Phone" value={previousResidence.landlordPhone} />}
            <InfoRow label="How long" value={previousResidence.howLongAtAddress} />
            <InfoRow label="Reason for leaving" value={previousResidence.reasonForLeaving} />
          </div>
        </div>

        {/* References, Pets & Vehicles */}
        <div style={s.card}>
          <div style={s.cardHead}>References, Pets & Vehicles</div>
          <div style={s.cardBody}>
            <div style={s.sectionTitle}>References ({references.length})</div>
            {references.length === 0 ? (
              <div style={{ ...s.emptyText, marginBottom: 10 }}>No references provided.</div>
            ) : (
              references.map((ref, idx) => (
                <div key={ref._id || `${ref.name}-${idx}`} style={idx === 0 ? {} : s.refItem}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", marginBottom: 4 }}>
                    {ref.name || "Unnamed reference"}
                  </div>
                  <InfoRow label="Relationship" value={ref.relationship} />
                  <InfoRow label="Address" value={ref.address} />
                  <InfoRow label="Phone" value={ref.phone} />
                  <InfoRow label="Employment" value={ref.placeOfEmployment} />
                  {ref.employmentAddress && <InfoRow label="Emp. Address" value={ref.employmentAddress} />}
                </div>
              ))
            )}

            <div style={{ ...s.sectionTitle, marginTop: 14 }}>Pets</div>
            {pets.hasPets ? (
              <>
                <InfoRow label="Type" value={pets.type} />
                <InfoRow label="Count" value={pets.count != null ? pets.count : undefined} />
              </>
            ) : (
              <div style={{ ...s.emptyText, marginBottom: 10 }}>No pets.</div>
            )}

            <div style={{ ...s.sectionTitle, marginTop: 14 }}>Vehicles ({vehicles.length})</div>
            {vehicles.length === 0 ? (
              <div style={s.emptyText}>No vehicles listed.</div>
            ) : (
              vehicles.map((vehicle, index) => (
                <div key={vehicle._id || index} style={index === 0 ? {} : s.vehicleItem}>
                  <InfoRow label="Type" value={vehicle.type} />
                  {vehicle.count != null && <InfoRow label="Count" value={vehicle.count} />}
                  {vehicle.plateNumber && <InfoRow label="Plate" value={vehicle.plateNumber} />}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetails;
